/**
 * PUWYLO Homepage Expansion — feature killswitch helpers.
 *
 * Read off the global UFE config (SSR-safe — populated on both server and client):
 *  - `digitalPuwyloHomepageExpansion` — the feature killswitch (gates all FE behavior).
 *
 * The batch API call is gated by the feature killswitch AND the CMS per-component flag:
 * a recap card opts in via `personalization.isPuwyloBatchEnabled` (surfaced by CSX), and the
 * batch thunk collects those flagged contexts as `puwyloBatchContextIds` (see
 * `utils/localStorage/P13n.js` → `getContextIdsToUpdate`). The batch is fetched only when the
 * expansion killswitch is on and at least one card opted in.
 */

import constants from 'constants/content';
import Empty from 'constants/empty';
import RvDataActions from 'actions/RvDataActions';
import analyticsConstants from 'analytics/constants';

const {
    RECAP_CAROUSEL: { ITEM_URLS }
} = constants;
const { readRvCategoryCookie } = RvDataActions;
const {
    CMS_COMPONENT_EVENTS: { PUWYLO_CONTROL_EXPERIENCE, PUWYLO_NO_TEST_EXPERIENCE }
} = analyticsConstants;

const KILLSWITCH_NAME = 'digitalPuwyloHomepageExpansion';

const KEEP_SHOPPING_SOT_CARD_TYPE = 'keep_shopping';
// Card 1 identity in the batch response (arch §2.3; distinct from the `keep_shopping`
// sotV453 value above). `withRecapListProps` derives `action.targetUrl = '/' + cardType`.
const PUWYLO_CARD1_CARD_TYPE = 'keep-shopping-for';
// AB-test exclusion signal that marks a CONTROL-group Card 1 in `excludedCards[]`.
const AB_TEST_CONTROL_REASON = 'AB_TEST_CONTROL';
// Up to 4 SKUs on the wire (spike §5 / PDE-2737).
const PUWYLO_MAX_SOT_SKUS = 4;

function isPuwyloHomepageExpansionEnabled() {
    return Sephora?.configurationSettings?.[KILLSWITCH_NAME]?.isEnabled === true;
}

/**
 * PUWYLO Card 1 (Keep Shopping For): builds the card-specific SOT decoration —
 * `sotV453` (cardType), `sotV454` (`;`-joined skuIds, up to 4), the `sotV439` bag
 * (`abTestId;abTestEndDate;abTestTraffic;recentCategory;skuCount`) + mirrored `timestamp`, and the
 * `sotV342/343/344` AB-test trio sourced from the batch card's `abTest` block. Runs for all
 * auth states (signed-in AND anonymous/unrecognized — BR-001). Generic recap vars
 * (`sotV310/311/312/233/315/316/317`) already flow downstream and are not set here.
 *
 * @param {object} item - Recap item; must use `ITEM_URLS.KEEP_SHOPPING` and may carry `abTest`/`products`.
 * @returns {{ sotV453: string, sotV454: string, sotV439: string, timestamp: string, sotV342: boolean, sotV343: boolean, sotV344: boolean, personalization: object }|null} SOT patch, or `null` if not Card 1.
 */
function buildKeepShoppingForSotFields(item) {
    if (item?.action?.targetUrl !== ITEM_URLS.KEEP_SHOPPING) {
        return null;
    }

    const products = Array.isArray(item.products) ? item.products : Empty.Array;
    const skuIds = products
        .map(product => product?.skuId)
        .filter(Boolean)
        .slice(0, PUWYLO_MAX_SOT_SKUS);
    const skuCount = skuIds.length;

    const abTest = item.abTest || Empty.Object;
    const abTestId = abTest.isAbTest ? abTest.abTestId || '' : '';
    const abTestEndDate = abTest.abTestEndDate ?? '';
    // abTestTraffic (batch abTest block, e.g. 50) — gated on isAbTest like abTestId; '' when no test.
    const abTestTraffic = abTest.isAbTest ? (abTest.abTestTraffic ?? '') : '';
    const recentCategory = readRvCategoryCookie()[0] || '';

    const group = abTest.isAbTest ? (abTest.isControl ? 'CONTROL' : 'CHALLENGER') : '';
    const keepShoppingSotV439Bundle =
        `abTestId:${abTestId};abTestEndDate:${abTestEndDate};abTestTraffic:${abTestTraffic};recentCategory:${recentCategory};skuCount:${skuCount}` +
        (group ? `;group:${group}` : '');

    return {
        sotV453: KEEP_SHOPPING_SOT_CARD_TYPE,
        sotV454: skuIds.join(';'),
        sotV439: keepShoppingSotV439Bundle,
        // banner.timestamp workaround, matches Replen / RTCO / Anonymous builders.
        timestamp: keepShoppingSotV439Bundle,
        sotV342: !!abTest.isDefault,
        sotV343: !!abTest.isControl,
        sotV344: !!abTest.isAbTest,
        personalization: {
            ...(item.personalization || Empty.Object),
            isDefault: !!abTest.isDefault,
            isControl: !!abTest.isControl,
            isAbTest: !!abTest.isAbTest,
            abTestEndDate: abTest.abTestEndDate || null
        }
    };
}

/**
 * Finds the Card 1 (Keep Shopping For) slot in a batch response list (`cards[]` or
 * `excludedCards[]`) by its `cardType`. SSR-safe; tolerant of non-array input.
 *
 * @param {Array} list normalized batch cards or raw excludedCards
 * @returns {object|undefined} the Card 1 entry, if present
 */
function findPuwyloCard1(list) {
    if (!Array.isArray(list)) {
        return undefined;
    }

    return list.find(entry => entry?.cardType === PUWYLO_CARD1_CARD_TYPE);
}

function resolvePuwyloCard1ContextId(response, requestedContextIds) {
    const card = findPuwyloCard1(response?.cards);
    const excluded = findPuwyloCard1(response?.excludedCards);

    return card?.contextId || excluded?.contextId || (Array.isArray(requestedContextIds) ? requestedContextIds[0] : '') || '';
}

function resolvePuwyloNoTestReason(card, excluded) {
    if (!card && !excluded) {
        return 'missing_context_id';
    }

    if (card && (!Array.isArray(card.products) || card.products.length === 0)) {
        return 'empty_products';
    }

    if (excluded) {
        return excluded.abTest?.isAbTest === true ? 'challenger_no_data' : 'no_test_no_data';
    }

    return 'missing_context_id';
}

// AB-test flag → SOT var mapping for the non-presentation payload. Named fields (not a positional
// trio) so the isDefault/isControl/isAbTest → sotV342/343/344 mapping is self-documenting and
// call sites can't silently transpose the flags.
function buildPuwyloBatchCard1SotFields({ ab, reason, flags }) {
    const abTestId = ab?.isAbTest ? ab.abTestId || '' : '';
    const abTestEndDate = ab?.abTestEndDate ?? '';
    // abTestTraffic (batch abTest block, e.g. 50) — gated on isAbTest like abTestId; '' when no test.
    const abTestTraffic = ab?.isAbTest ? (ab.abTestTraffic ?? '') : '';
    const recentCategory = readRvCategoryCookie()[0] || '';

    const group = flags.isAbTest ? (flags.isControl ? 'CONTROL' : 'CHALLENGER') : '';
    const bag =
        `abTestId:${abTestId};abTestEndDate:${abTestEndDate};abTestTraffic:${abTestTraffic};recentCategory:${recentCategory};skuCount:0` +
        (group ? `;group:${group}` : '') +
        (reason ? `;reason:${reason}` : '');

    return {
        sotV453: KEEP_SHOPPING_SOT_CARD_TYPE,
        sotV454: '',
        sotV439: bag,
        // banner.timestamp workaround, matches Replen / RTCO / Anonymous builders.
        timestamp: bag,
        sotV342: flags.isDefault,
        sotV343: flags.isControl,
        sotV344: flags.isAbTest,
        action: { targetUrl: ITEM_URLS.KEEP_SHOPPING }
    };
}

function buildPuwyloBatchCard1SotEvent({ response } = {}) {
    const card = findPuwyloCard1(response?.cards);
    const excluded = findPuwyloCard1(response?.excludedCards);

    const isControl = card?.abTest?.isControl === true || excluded?.abTest?.isControl === true || excluded?.reason === AB_TEST_CONTROL_REASON;

    // Control precedence: a control slot always fires the control event, even with products.
    if (isControl) {
        const ab = card?.abTest ?? excluded?.abTest;

        return {
            sotType: PUWYLO_CONTROL_EXPERIENCE,
            sotFields: buildPuwyloBatchCard1SotFields({ ab, reason: null, flags: { isDefault: false, isControl: true, isAbTest: true } })
        };
    }

    // Renderable challenger card (products present, not control) → no new event.
    if (card && Array.isArray(card.products) && card.products.length > 0) {
        return null;
    }

    // No-test experience: challenger-no-data, no-test-no-data, empty products, or missing context.
    const ab = card?.abTest ?? excluded?.abTest;
    const isChallenger = ab?.isAbTest === true && ab?.isControl !== true;
    const flags = isChallenger ? { isDefault: false, isControl: false, isAbTest: true } : { isDefault: true, isControl: false, isAbTest: false };
    const reason = resolvePuwyloNoTestReason(card, excluded);

    return {
        sotType: PUWYLO_NO_TEST_EXPERIENCE,
        sotFields: buildPuwyloBatchCard1SotFields({ ab, reason, flags })
    };
}

/**
 * Selects the PUWYLO batch cards eligible to display in the carousel: excludes cards
 * with no products (no empty slots) and Control Group cards (abTest.isControl=true)
 * while preserving the order received from the batch API. The backend returns cards
 * in the Contentful-configured order (BR-020), so the frontend renders them as-received
 * and does NOT re-sort. Pure + SSR-safe; never mutates the input array.
 *
 * @param {Array} cards normalized batch cards ({ contextId, displayOrder, products, abTest, ... })
 * @returns {Array} filtered copy in received order
 */
function getEligiblePuwyloCards(cards) {
    if (!Array.isArray(cards)) {
        return [];
    }

    return cards.filter(card => {
        const hasProducts = Array.isArray(card?.products) && card.products.length > 0;
        const isControl = card?.abTest?.isControl === true && card?.abTest?.isAbTest === true;

        // Exclude Control Group cards (they fire impression separately)
        return hasProducts && !isControl;
    });
}

/**
 * Decides whether the enhanced PUWYLO carousel should render. True only when the
 * killswitch is on AND `batchData` is non-null AND at least one card survives
 * the empty-product filter. Otherwise false → existing carousel / fallback path.
 *
 * @param {Object|null} batchData normalized batch payload or null fallback sentinel
 * @returns {boolean}
 */
function shouldUsePuwyloCarousel(batchData) {
    if (!isPuwyloHomepageExpansionEnabled() || !batchData) {
        return false;
    }

    return getEligiblePuwyloCards(batchData.cards).length > 0;
}

export default {
    isPuwyloHomepageExpansionEnabled,
    getEligiblePuwyloCards,
    shouldUsePuwyloCarousel,
    buildKeepShoppingForSotFields,
    buildPuwyloBatchCard1SotEvent,
    resolvePuwyloCard1ContextId
};
